import LocalStorage, { StorageKeys } from 'utils/local-storage';

export interface StoredOrderId {
  id: string;
  lastUpdate: number;
}

// Based on Amplitude the average time to convert on /cart/payment is 15s so 5
// minutes should be more than enough
export const ORDER_TTL = 5 * 60 * 1000;

/**
 * Retrieves and validates the stored `rbiOrderId` to ensure its consistency
 * between page loads.
 *
 * The function checks if the current `rbiOrderId` exists in the cart context
 * and whether it matches or conflicts with the stored version in
 * `LocalStorage`. If the stored ID is valid and matches, it sets the
 * `rbiOrderId` in context.  If the stored ID has expired or differs, it updates
 * or clears the stored data as needed.
 *
 * @param {string | null} rbiOrderId - The current order ID from context, or
 * null if not available.
 * @param {(value: string) => void} setRbiOrderId - A setter function to update
 * the `rbiOrderId` in context.
 * @returns {boolean} - Returns true if a valid `rbiOrderId` is successfully set
 * or retrieved; otherwise, returns false.
 *
 * Side Effects:
 * - Updates or clears `LocalStorage` depending on the validity and expiration
 *   of the stored order ID.
 * - Sets the `rbiOrderId` in context if a valid ID is retrieved from storage.
 */
export function isRbiOrderExpired(
  rbiOrderId: string | null,
  setRbiOrderId: (value: string) => void
): boolean {
  const now = new Date().getTime();
  const stored = LocalStorage.getItem<StoredOrderId>(StorageKeys.ORDER_ID);

  // If no rbiOrderId and no stored value, return false
  if (!rbiOrderId && !stored) {
    return false;
  }

  // If no rbiOrderId but stored value exists
  if (!rbiOrderId && stored) {
    if (stored.lastUpdate < now - ORDER_TTL) {
      // Stored order is expired so clear the local storage and return false
      LocalStorage.removeItem(StorageKeys.ORDER_ID);
      return false;
    }

    setRbiOrderId(stored.id);
    return true;
  }

  if (rbiOrderId && !stored) {
    // rbiOrderId exists but no stored value so store it with the current timestamp
    LocalStorage.setItem(StorageKeys.ORDER_ID, { id: rbiOrderId, lastUpdate: now });
    return true;
  }

  if (rbiOrderId && stored) {
    // If stored order ID doesn't match current rbiOrderId, update the stored value
    if (stored.id !== rbiOrderId) {
      LocalStorage.setItem(StorageKeys.ORDER_ID, { id: rbiOrderId, lastUpdate: now });
      return true;
    }

    // If the stored order is expired, clear the local storage and return false
    if (stored.lastUpdate < now - ORDER_TTL) {
      LocalStorage.removeItem(StorageKeys.ORDER_ID);
      return false;
    }

    // Otherwise, the stored data is valid
    return true;
  }

  return false;
}
